import React, { useId, useRef, useState } from 'react';

import { Input } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import NumericInputField from 'components/common/NumericInputField';
import {
  PADDOCKS_ARABLE_AREA_LABEL,
  PADDOCKS_AREA_LABEL,
  PADDOCKS_NOTES_LABEL,
  PADDOCKS_UNIT_LABEL,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';

import ShortcutHint from 'components/common/ShortcutHint/ShortcutHint';

import AreaUnitSelect from './AreaUnitSelect';
import { GridFieldError } from './GridCellInputs';
import GridVoiceMic from './GridVoiceMic';

export interface DraftRowValues {
  name: string;
  area: number | null;
  /** Paddocks only. */
  arableArea: number | null;
  areaUnit: IUnit | null;
  notes: string | null;
}

interface DraftRowProps {
  kind: 'farm' | 'paddock';
  /** Name field placeholder and accessible name, e.g. "Paddock name". */
  namePlaceholder: string;
  requiredMessage: string;
  defaultAreaUnit: IUnit | null;
  gUnits: GUnit[];
  cellClassName: string;
  rowClassName?: string;
  onSave: (values: DraftRowValues) => void;
  onCancel: () => void;
}

/**
 * A new farm or paddock as a whole grid row (OP-2481): the cursor starts in
 * the name, and area, arable area, unit and notes can be filled in before
 * it's saved. Enter, or leaving the row, saves it; Escape, or leaving it
 * untouched, drops it. It stays at the bottom of its farm until saved, then
 * sorts into place.
 */
const DraftRow: React.FC<DraftRowProps> = ({
  kind,
  namePlaceholder,
  requiredMessage,
  defaultAreaUnit,
  gUnits,
  cellClassName,
  rowClassName,
  onSave,
  onCancel,
}) => {
  const [values, setValues] = useState<DraftRowValues>({
    name: '',
    area: null,
    arableArea: null,
    areaUnit: defaultAreaUnit,
    notes: null,
  });
  const [error, setError] = useState<string | null>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const notesRef = useRef<TextAreaRef>(null);
  const finishedRef = useRef(false);
  const areaId = useId();
  const arableId = useId();

  const patch = (next: Partial<DraftRowValues>) =>
    setValues((prev) => ({ ...prev, ...next }));

  const isUntouched =
    !values.name.trim() &&
    values.area === null &&
    values.arableArea === null &&
    !values.notes?.trim();

  const finish = () => {
    if (finishedRef.current) return;
    if (!values.name.trim()) {
      if (isUntouched) {
        finishedRef.current = true;
        onCancel();
      } else {
        setError(requiredMessage);
      }
      return;
    }
    finishedRef.current = true;
    onSave({ ...values, name: values.name.trim() });
  };

  // Leaving the row — not just moving between its fields — finishes it. The
  // check waits a tick, as the unit picker's list moves focus briefly.
  const handleBlur = () =>
    window.setTimeout(() => {
      if (!rowRef.current?.contains(document.activeElement)) finish();
    }, 0);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.target as HTMLElement).closest('.ant-select')) return;
    // Shift+Enter is a new line in the notes; Enter saves the row.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      finish();
    } else if (event.key === 'Escape') {
      finishedRef.current = true;
      onCancel();
    }
  };

  const fieldLabel = (field: string) =>
    `${field}, ${values.name.trim() || namePlaceholder}`;

  return (
    <tr
      ref={rowRef}
      className={`paddocks-grid-row ${rowClassName ?? ''}`}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <td className={cellClassName} />
      <td className={`${cellClassName} stack-primary`}>
        <div
          className={`paddocks-grid-field paddocks-grid-draft-name relative ${kind === 'paddock' ? 'paddocks-grid-indent' : 'font-semibold'}`}
          data-grid-col="name"
        >
          <Input
            autoFocus
            value={values.name}
            placeholder={namePlaceholder}
            aria-label={namePlaceholder}
            aria-invalid={!!error}
            status={error ? 'error' : undefined}
            onChange={(e) => {
              patch({ name: e.target.value });
              if (error) setError(null);
            }}
          />
          {/* How to finish the row, as Rotation's cell editor shows it. */}
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
            <ShortcutHint shortcutKey="Enter" variant="subtle" />
          </span>
          {error && <GridFieldError message={error} />}
        </div>
      </td>
      <td className={cellClassName} data-label={PADDOCKS_AREA_LABEL}>
        <div className="paddocks-grid-field" data-grid-col="area">
          <label htmlFor={areaId} className="sr-only">
            {fieldLabel(PADDOCKS_AREA_LABEL)}
          </label>
          <NumericInputField
            id={areaId}
            value={values.area}
            min={0}
            precision={2}
            controls={false}
            keyboard={false}
            size="middle"
            inputClassName="w-full"
            onChange={(area) => patch({ area })}
          />
        </div>
      </td>
      <td
        className={`${cellClassName} ${kind === 'farm' ? 'stack-empty' : ''}`}
        data-label={kind === 'paddock' ? PADDOCKS_ARABLE_AREA_LABEL : undefined}
      >
        {kind === 'paddock' && (
          <div className="paddocks-grid-field" data-grid-col="arableArea">
            <label htmlFor={arableId} className="sr-only">
              {fieldLabel(PADDOCKS_ARABLE_AREA_LABEL)}
            </label>
            <NumericInputField
              id={arableId}
              value={values.arableArea}
              min={0}
              precision={2}
              controls={false}
              keyboard={false}
              size="middle"
              inputClassName="w-full"
              onChange={(arableArea) => patch({ arableArea })}
            />
          </div>
        )}
      </td>
      <td className={cellClassName} data-label={PADDOCKS_UNIT_LABEL}>
        <AreaUnitSelect
          value={values.areaUnit}
          gUnits={gUnits}
          ariaLabel={fieldLabel(PADDOCKS_UNIT_LABEL)}
          onChange={(areaUnit) => patch({ areaUnit })}
        />
      </td>
      <td
        className={`${cellClassName} stack-full`}
        data-label={PADDOCKS_NOTES_LABEL}
      >
        <div className="paddocks-grid-field" data-grid-col="notes">
          <Input.TextArea
            ref={notesRef}
            value={values.notes ?? ''}
            autoSize={{ minRows: 1, maxRows: 6 }}
            aria-label={fieldLabel(PADDOCKS_NOTES_LABEL)}
            onChange={(e) => patch({ notes: e.target.value || null })}
          />
          <GridVoiceMic
            textAreaRef={notesRef}
            text={values.notes ?? ''}
            onText={(notes) => patch({ notes: notes || null })}
          />
        </div>
      </td>
    </tr>
  );
};

export default DraftRow;
